// src/components/common/Files.jsx
import React, { useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  InputGroup,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,
  Badge,
  Alert,
} from "reactstrap";
import { AiOutlineInfoCircle } from "react-icons/ai";

// Tooltip icon
const InfoTooltip = ({ id, content, placement = "top" }) => (
  <>
    <AiOutlineInfoCircle
      data-tooltip-id={id}
      title={content}
      style={{
        marginLeft: "4px",
        cursor: "help",
        color: "#6B7280",
        fontSize: "16px",
        verticalAlign: "middle",
        display: "inline-flex",
        alignItems: "center",
      }}
    />
  </>
);

const Files = ({
  files = [],
  selectedFiles = [],
  onToggleFile,
  onSelectAllGroupFiles,
  onToggleFileWithShift, // New prop for range selection
  onToggleAll,
  title = "Files",
  searchable = true,
  selectable = true,
  filesPerPage = 30,
  columns = 6,
  showFileCount = true,
  showSelectAll = true,
  showInstructions = true,
  customSearchFields = ["file_name", "file_description"], // Configurable search fields
  style = {}, // Custom container style
  height = "200px", // Configurable height
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastSelectedFileIndex, setLastSelectedFileIndex] = useState(null);

  // Filter files based on search term and configurable fields
  const filteredFiles = useMemo(() => {
    if (!searchTerm.trim() || !searchable) return files;

    return files.filter((file) =>
      customSearchFields.some((field) =>
        file[field]?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [files, searchTerm, customSearchFields, searchable]);

  // Pagination
  const totalPages = Math.ceil(filteredFiles.length / filesPerPage);
  const startIndex = (currentPage - 1) * filesPerPage;
  const paginatedFiles = filteredFiles.slice(
    startIndex,
    startIndex + filesPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Enhanced file click handler with range selection support
  const handleFileClick = useCallback(
    (e, fileId, fileIndex) => {
      if (!selectable) return;

      const isShiftPressed = e.shiftKey;
      const globalIndex = startIndex + fileIndex; // Calculate global index for range selection

      if (onToggleFileWithShift && isShiftPressed) {
        // Use range selection if available
        onToggleFileWithShift(
          fileId,
          isShiftPressed,
          globalIndex,
          filteredFiles
        );
        setLastSelectedFileIndex(globalIndex);
      } else {
        // Normal file toggle
        if (onToggleFile) {
          onToggleFile(fileId);
        }
        setLastSelectedFileIndex(globalIndex);
      }
    },
    [selectable, onToggleFileWithShift, onToggleFile, startIndex, filteredFiles]
  );

  // Handle select all functionality
  const handleSelectAll = useCallback(() => {
    if (!onToggleAll || !selectable) return;

    const fileIds = filteredFiles.map((f) => f.file_id);
    const allSelected = fileIds.every((id) => selectedFiles.includes(id));

    if (allSelected) {
      // Deselect all files
      onToggleAll(fileIds, false);
    } else {
      // Select all files
      onToggleAll(fileIds, true);
    }
  }, [filteredFiles, selectedFiles, onToggleAll, selectable]);

  // When called will check all visible checkboxes in the files box
  const checkAllVisibleCheckboxes = useCallback(() => {
    onSelectAllGroupFiles(true);
  }, [filteredFiles, selectedFiles]);

  // When called will uncheck all visible checkboxes in the files box
  const uncheckAllVisibleCheckboxes = useCallback(() => {
    onSelectAllGroupFiles(false);
  }, [filteredFiles, selectedFiles]);

  // Mouse interaction handlers for better UX
  const handleFileMouseEnter = useCallback(
    (e, isSelected) => {
      if (!isSelected && selectable) {
        e.currentTarget.style.backgroundColor = "#f9fafb";
        e.currentTarget.style.borderColor = "#d1d5db";
      }
    },
    [selectable]
  );

  const handleFileMouseLeave = useCallback(
    (e, isSelected) => {
      if (!isSelected && selectable) {
        e.currentTarget.style.backgroundColor = "white";
        e.currentTarget.style.borderColor = "#e5e7eb";
      }
    },
    [selectable]
  );
  const renderPaginationItems = () => {
    const items = [];

    // Previous button
    items.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink
          previous
          onClick={() => handlePageChange(currentPage - 1)}
          style={{
            backgroundColor: "#f8f9fa",
            borderColor: "#d1d5db",
            color: "#6b7280",
          }}
        />
      </PaginationItem>
    );

    // Page numbers
    for (let i = 1; i <= Math.min(totalPages, 5); i++) {
      items.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            style={{
              backgroundColor: i === currentPage ? "#2F9E37" : "#ffffff",
              borderColor: i === currentPage ? "#2F9E37" : "#d1d5db",
              color: i === currentPage ? "#ffffff" : "#374151",
            }}
          >
            {String(i).padStart(2, "0")}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (totalPages > 5) {
      items.push(
        <PaginationItem key="ellipsis" disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
      items.push(
        <PaginationItem key="last">
          <PaginationLink
            onClick={() => handlePageChange(totalPages)}
            style={{
              backgroundColor: "#ffffff",
              borderColor: "#d1d5db",
              color: "#374151",
            }}
          >
            {String(totalPages).padStart(2, "0")}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    items.push(
      <PaginationItem key="next" disabled={currentPage === totalPages}>
        <PaginationLink
          next
          onClick={() => handlePageChange(currentPage + 1)}
          style={{
            backgroundColor: "#f8f9fa",
            borderColor: "#d1d5db",
            color: "#6b7280",
          }}
        />
      </PaginationItem>
    );

    return items;
  };

  return (
    <Card
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "4px",
        height: "100%",
        ...style,
      }}
    >
      <CardHeader className="d-flex justify-content-between align-items-center flex-wrap">
        {/* Left side - Title and file count */}
        <div className="d-flex align-items-center">
          <h5 className="mb-0" style={{ color: "#1a6d51" }}>
            <i className="ri-file-list-3-line me-2"></i>
            {title}
          </h5>
        </div>

        {/* Right side - Controls */}
        <div className="d-flex align-items-center gap-2">
          {/* Uncheck All Button */}
          <Button
            id="uncheckVisbleBtn"
            size="sm"
            color="outline-danger"
            onClick={ uncheckAllVisibleCheckboxes }
          >
          Uncheck All
          </Button>

          {/* Check All Button */}
          <Button
            id="checkVisbleBtn"
            size="sm"
            color="outline-primary"
            onClick={ checkAllVisibleCheckboxes }
          >
          Check All        
          </Button>
          <InfoTooltip
            id="checkbox-shortcut-btn-tt"
            content="Uncheck or Check all files with the currently 
                     selected group and seq-type"
          />

          {/* Select All Button */}
          {showSelectAll && selectable && onToggleAll && (
            <Button
              size="sm"
              color="outline-primary"
              onClick={handleSelectAll}
              disabled={filteredFiles.length === 0}
              style={{
                fontSize: "12px",
                padding: "4px 12px",
                borderRadius: "6px",
              }}
            >
              {filteredFiles
                .map((f) => f.file_id)
                .every((id) => selectedFiles.includes(id)) &&
              filteredFiles.length > 0
                ? "Deselect All"
                : "Select All"}
            </Button>
          )}

          {/* Search Input */}
          {searchable && (
            <InputGroup style={{ width: "250px" }}>
              <InputGroupText style={{ borderColor: "#d1d5db" }}>
                <i className="ri-search-line text-muted" />
              </InputGroupText>
              <Input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  borderRadius: "0 6px 6px 0",
                  fontSize: "13px",
                  borderColor: "#d1d5db",
                }}
              />
            </InputGroup>
          )}
        </div>
      </CardHeader>

      <CardBody style={{ padding: "20px" }}>
        {/* Instructions for range selection */}
        {showInstructions &&
          selectable &&
          onToggleFileWithShift &&
          filteredFiles.length > 0 && (
            <Alert
              color="info"
              className="mb-3"
              style={{ fontSize: "12px", padding: "8px 12px" }}
            >
              <i className="ri-information-line me-2"></i>
              <strong>Selection Guide:</strong> Click files to select/deselect
              them. Use Shift+Click for range selection.
            </Alert>
          )}

        {/* Files Container with GroupsManager styling */}
        <div
          style={{
            // height: height,
            // minHeight: "100px",
            overflowY: "auto",
            borderRadius: "6px",
            padding: "1px",
            backgroundColor: "#fafafa",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: "6px",
            }}
          >
            {paginatedFiles.map((file, index) => {
              const isSelected = selectedFiles.includes(file.file_id);

              return (
                <div
                  key={file.file_id}
                  onClick={(e) => handleFileClick(e, file.file_id, index)}
                  onMouseEnter={(e) => handleFileMouseEnter(e, isSelected)}
                  onMouseLeave={(e) => handleFileMouseLeave(e, isSelected)}
                  style={{
                    padding: "6px",
                    border: isSelected
                      ? "1px solid #2F9E37"
                      : "1px solid #e5e7eb",
                    borderRadius: "4px",
                    backgroundColor: isSelected ? "#f0fdf4" : "white",
                    cursor: selectable ? "pointer" : "default",
                    fontSize: "12px",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    minHeight: "32px",
                  }}
                >
                  {selectable && (
                    <Input
                      type="checkbox"
                      checked={isSelected}
                      value={file.file_id}
                      readOnly
                      style={{
                        pointerEvents: "none",
                        margin: 0,
                        width: "14px",
                        height: "14px",
                      }}
                    />
                  )}
                  <span
                    style={{
                      fontSize: "11px",
                      lineHeight: "1.2",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                    title={
                      file.file_name || file.name || `File ${file.file_id}`
                    }
                  >
                    {file.file_name || file.name || `File ${file.file_id}`}
                  </span>
                </div>
              );
            })}

            {/* Empty state message */}
            {filteredFiles.length === 0 && (
              <div
                className="text-muted text-center"
                style={{
                  gridColumn: "1 / -1",
                  padding: "20px",
                  fontSize: "14px",
                }}
              >
                {searchTerm
                  ? "No files match your search"
                  : "No files available"}
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              Page {currentPage} of {totalPages}
            </small>
            <Pagination size="sm">{renderPaginationItems()}</Pagination>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default Files;
